import { useState, useRef, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "../../../api";
import {
  InvocationStatusEnum,
  type WorkPatternImportInput,
  type WorkPatternRow,
  type MyPayEnvironment,
} from "../../../generated/core-api";
import {
  useCompanyProvisionAgentId,
  isTerminalInvocationStatus,
} from "../../../onboarding";
import {
  computeStandardValuesMismatchMessage,
  recomputeParseErrorsAfterPatternEdit,
  type WorkPatternImportWarning,
  type WorkPatternImportParseFailedRow,
} from "./useWorkPatternImportCsvParser";
import type { ScopeTypeOption } from "../../types";
import { clientParseErrorsByPatternIndex } from "../../components/WorkPatternImport/workPatternImportClustering";
import { mergeClientAndServerErrorParts } from "../../components/WorkPatternImport/workPatternImportErrorMerge";

export type { WorkPatternImportParseFailedRow };

// ---------------------------------------------------------------------------
// Local result types — WorkPatternImportResult is returned via the poll
// endpoint as `result: unknown`; define the shape here.
// ---------------------------------------------------------------------------

export interface WorkPatternImportError {
  index: number;
  workPatternName: string;
  error: string;
}

/** Per-row advisory from the work_pattern_import workflow (Christmas). */
export interface WorkPatternImportApiWarning {
  index: number;
  workPatternName: string;
  warning: string;
}

export interface WorkPatternImportResult {
  total: number;
  succeeded: number;
  failed: number;
  errors: WorkPatternImportError[];
  /** Per-row Christmas advisories. Omitted only in tests that don't set a poll result. */
  warnings?: WorkPatternImportApiWarning[];
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkPatternImportManagerReturn {
  pendingPatterns: WorkPatternRow[];
  parseErrors: string[];
  /** Per-row StandardHours/StandardDays mismatch warnings from the CSV parser. */
  parseWarnings: WorkPatternImportWarning[];
  /** Keyed by pattern index (position in pendingPatterns). */
  warningsByPatternIndex: Map<number, string[]>;
  hasParseWarnings: boolean;
  /** Company code currently associated with the pending batch. */
  companyCode: string | null;
  /** Scope type currently associated with the pending batch. */
  scopeType: ScopeTypeOption | null;
  /** Target MyPay API deployment for validate/commit invocations. */
  mypayEnvironment: MyPayEnvironment | null;
  /** Raw CSV text from the uploaded file — used to build the errors CSV. */
  rawCsvText: string | null;
  /** Set after a successful validate-only pre-flight (workflow COMPLETED). */
  validationReport: WorkPatternImportResult | null;
  /** Set after a successful commit call (workflow COMPLETED). */
  commitResult: WorkPatternImportResult | null;
  /** True after the user edits a pattern — commit is blocked until re-validation (R14). */
  validationStale: boolean;
  /** Pattern indices whose StandardHours/StandardDays warnings the user chose to ignore (R14). */
  ignoredWarningIndices: ReadonlySet<number>;
  /** True when mismatch warnings remain that the user has not ignored (R14). */
  hasUnacknowledgedWarnings: boolean;
  /** Surface-level error string for agent errors, not-configured, etc. */
  managerError: string | null;
  isValidating: boolean;
  isCommitting: boolean;
  hasData: boolean;
  hasParseErrors: boolean;
  /**
   * True while an upload/validate session is in progress and commit has not
   * completed — used to block in-app navigation (#400).
   */
  hasUncommittedImportProgress: boolean;
  /** Rows from the CSV that failed client-side parse — merged into the validation report. */
  parseFailedRows: WorkPatternImportParseFailedRow[];
  /** CSV row numbers aligned with `pendingPatterns` (row 2 = first data row). */
  patternCsvRowNumbers: number[];
  /**
   * True when valid patterns were sent to Christmas but no usable validation
   * report was produced (invoke failure, poll failure, timeout, bad payload).
   */
  validationEndedWithoutReport: boolean;
  /**
   * Accept output from the CSV parser hook.
   *
   * Shared by Track 1 (`useWorkPatternImportInvocation`) and Track 2
   * (`useWorkPatternImportWorkloadsManager`) so the screen can switch behind
   * the cutover flag without branching on parameter lists.
   *
   * - `rawCsvText` (#414): original file text for the errors-CSV download.
   *   Track 1 stores it; Track 2 stores it for the same UI affordance.
   * - `file` (LADR-039 / #440): raw `File` for Track 2 multipart
   *   `createOnboardingImport` (server owns canonical CSV parse). Track 1
   *   accepts and ignores it.
   * - `dayColumnCountFromHeader`: the number of day columns detected in the
   *   CSV header. Track 1 uses it to recompute the header-vs-CycleDays
   *   mismatch warning on each `updatePattern` edit; Track 2 accepts and
   *   ignores it (not part of its validate/commit pipeline — the server
   *   re-validates the imported rows itself).
   */
  setPendingData: (
    patterns: WorkPatternRow[],
    errors: string[],
    warnings?: WorkPatternImportWarning[],
    companyCode?: string | null,
    scopeType?: ScopeTypeOption | null,
    parseFailedRows?: WorkPatternImportParseFailedRow[],
    patternCsvRowNumbers?: number[],
    rawCsvText?: string | null,
    file?: File,
    dayColumnCountFromHeader?: number
  ) => void;
  /**
   * Pre-flight: invokes the `work_pattern_import` workflow with `mode: "validate"`.
   * Mints a fresh idempotency key reused by commit().
   * Sets validationReport once the workflow reaches COMPLETED.
   */
  validate: () => Promise<void>;
  /**
   * Commit: invokes the `work_pattern_import` workflow with `mode: "commit"`,
   * reusing the idempotency key from validate().
   * Sets commitResult once the workflow reaches COMPLETED.
   * Invalidates the work-patterns query cache on success.
   */
  commit: (options?: WorkPatternImportCommitOptions) => Promise<void>;
  clear: () => void;
  removePattern: (index: number) => void;
  updatePattern: (index: number, next: WorkPatternRow) => void;
  /** Dismiss R13 standard-values warning for one row (R14 Ignore). */
  ignoreWarning: (index: number) => void;
  setMyPayEnvironment: (environment: MyPayEnvironment | null) => void;
}

export interface WorkPatternImportCommitOptions {
  /** Treat all remaining mismatch warnings as dismissed for this commit (R14). */
  overrideUnacknowledgedWarnings?: boolean;
}

// ---------------------------------------------------------------------------
// Type guard — narrows AgentInvocationStatus.result (typed unknown) to the
// work_pattern_import workflow's own result shape.
// ---------------------------------------------------------------------------

interface WorkPatternImportWorkflowResult {
  status: string;
  mode: "validate" | "commit";
  total: number;
  succeeded: number;
  failed: number;
  errors: Array<{ index: number; workPatternName: string; error: string }>;
  warnings?: unknown;
}

export function isWorkPatternImportResult(
  value: unknown
): value is WorkPatternImportWorkflowResult {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.total === "number" &&
    typeof candidate.succeeded === "number" &&
    typeof candidate.failed === "number" &&
    Array.isArray(candidate.errors) &&
    (candidate.warnings === undefined || Array.isArray(candidate.warnings)) &&
    (candidate.mode === "validate" || candidate.mode === "commit")
  );
}

function readApiWarnings(value: unknown): WorkPatternImportApiWarning[] {
  if (!Array.isArray(value)) return [];
  const warnings: WorkPatternImportApiWarning[] = [];
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue;
    const row = item as Record<string, unknown>;
    const workPatternName =
      typeof row.workPatternName === "string"
        ? row.workPatternName
        : typeof row.work_pattern_name === "string"
          ? row.work_pattern_name
          : null;
    const warning = typeof row.warning === "string" ? row.warning : null;
    if (
      typeof row.index !== "number" ||
      workPatternName === null ||
      warning === null
    ) {
      continue;
    }
    warnings.push({ index: row.index, workPatternName, warning });
  }
  return warnings;
}

function toLocalImportResult(
  result: WorkPatternImportWorkflowResult,
  parseFailCount = 0
): WorkPatternImportResult {
  return {
    total: result.total + parseFailCount,
    succeeded: result.succeeded,
    failed: result.failed + parseFailCount,
    errors: result.errors,
    warnings: readApiWarnings(result.warnings),
  };
}

/**
 * Skipped blocking-error row recorded at commit time so it can be injected
 * into the commit result as a failed entry.
 */
export interface SkippedBlockingRow {
  originalIndex: number;
  workPatternName: string;
  error: string;
}

/** @internal Exported for unit testing only. */
export { toCommitImportResult };

/**
 * Build the commit result for display when a partial commit was performed.
 *
 * - Maps workflow error indices (relative to the submitted eligible subset)
 *   back to the original pendingPatterns indices.
 * - Prepends the skipped blocking rows as failed entries with their original
 *   indices so the clustered report maps them correctly.
 * - Remaps api warning indices the same way.
 * - Recomputes totals to cover the full batch (eligible + skipped + parse-failed).
 */
function toCommitImportResult(
  result: WorkPatternImportWorkflowResult,
  eligibleIndices: number[],
  skippedRows: SkippedBlockingRow[],
  parseFailCount = 0
): WorkPatternImportResult {
  // Remap workflow errors: index into eligible subset → original index.
  const remappedErrors: WorkPatternImportError[] = result.errors.map((e) => ({
    index: eligibleIndices[e.index] ?? e.index,
    workPatternName: e.workPatternName,
    error: e.error,
  }));

  // Inject skipped rows as failures at the start.
  const skippedErrors: WorkPatternImportError[] = skippedRows.map((s) => ({
    index: s.originalIndex,
    workPatternName: s.workPatternName,
    error: s.error,
  }));

  // Remap api warnings: index into eligible subset → original index.
  const remappedWarnings: WorkPatternImportApiWarning[] = readApiWarnings(
    result.warnings
  ).map((w) => ({
    index: eligibleIndices[w.index] ?? w.index,
    workPatternName: w.workPatternName,
    warning: w.warning,
  }));

  const totalFailed =
    skippedRows.length + result.failed + parseFailCount;
  const totalAll =
    eligibleIndices.length + skippedRows.length + parseFailCount;

  return {
    total: totalAll,
    succeeded: result.succeeded,
    failed: totalFailed,
    errors: [...skippedErrors, ...remappedErrors],
    warnings: remappedWarnings,
  };
}

/**
 * Exported (Step 8) so `useWorkPatternImportWorkloadsManager` can reuse the
 * exact same parse-warning + API-warning merge semantics instead of
 * reimplementing this `Map`-building logic — it is pure and has no Track
 * 1-specific coupling.
 */
export function mergeWarningsByIndex(
  parseWarnings: WorkPatternImportWarning[],
  apiWarnings: WorkPatternImportApiWarning[]
): Map<number, string[]> {
  const map = new Map<number, string[]>();
  const add = (index: number, message: string) => {
    const existing = map.get(index);
    if (existing) {
      if (!existing.includes(message)) {
        existing.push(message);
      }
    } else {
      map.set(index, [message]);
    }
  };
  for (const warning of parseWarnings) {
    add(warning.patternIndex, warning.message);
  }
  // Christmas re-computes the same R13 standard-values mismatch the client CSV
  // parser already produces (proven by the `computeStandardValuesMismatchMessage
  // matches parseCSVText` parser test), just with different wording — so merging
  // both would double-word the row (e.g. "StandardHours is 38 but the day pattern
  // averages 40 hours/week · Declared StandardHours (38) does not match the
  // calculated value (40 hours per week)."). The client warning wins because it
  // drives the R14 Ignore/acknowledge workflow (`hasUnacknowledgedWarnings`,
  // `ignoreWarning`), so we skip any API warning on a row that already carries a
  // client parse warning.
  //
  // ASSUMPTION / TRADEOFF: this treats every Christmas warning on a
  // client-warned row as a redundant R13 duplicate. Today that holds — the only
  // warning types are R13 (both layers compute it) and decimal-rounding
  // (client-only), so no Christmas-exclusive warning can be hidden. If Christmas
  // ever emits a genuinely different warning kind, it would be suppressed on any
  // row that also has a client warning; revisit this dedup (e.g. match on warning
  // kind) if that happens.
  for (const warning of apiWarnings) {
    if (!map.has(warning.index)) {
      add(warning.index, warning.warning);
    }
  }
  return map;
}

/**
 * Whether a validate attempt finished without a usable validation report while
 * valid patterns remain in the batch (#407 parse-error banner fallback).
 */
export function computeValidationEndedWithoutReport(args: {
  pendingPatternCount: number;
  validationReport: WorkPatternImportResult | null;
  isValidating: boolean;
  isCommitting: boolean;
  validateWorkflowId: string | null;
  validateTimedOut: boolean;
  validatePollStatus: InvocationStatusEnum | undefined;
  validatePollResult: unknown;
  validateInvokeFailed: boolean;
}): boolean {
  const {
    pendingPatternCount,
    validationReport,
    isValidating,
    isCommitting,
    validateWorkflowId,
    validateTimedOut,
    validatePollStatus,
    validatePollResult,
    validateInvokeFailed,
  } = args;

  if (pendingPatternCount === 0) {
    return false;
  }
  if (validationReport !== null) {
    return false;
  }
  if (isValidating || isCommitting) {
    return false;
  }
  if (validateInvokeFailed && !validateWorkflowId) {
    return true;
  }
  if (!validateWorkflowId) {
    return false;
  }
  if (validateTimedOut) {
    return true;
  }
  if (!validatePollStatus || !isTerminalInvocationStatus(validatePollStatus)) {
    return false;
  }
  if (validatePollStatus !== InvocationStatusEnum.COMPLETED) {
    return true;
  }
  return !isWorkPatternImportResult(validatePollResult);
}

/**
 * Exported (Step 8) alongside `mergeWarningsByIndex` so
 * `useWorkPatternImportWorkloadsManager` can compute the same R14
 * unacknowledged-warnings gate without duplicating this logic.
 */
export function computeHasUnacknowledgedWarnings(
  warningsByPatternIndex: Map<number, string[]>,
  ignoredWarningIndices: ReadonlySet<number>
): boolean {
  for (const [index, messages] of warningsByPatternIndex) {
    if ((messages?.length ?? 0) > 0 && !ignoredWarningIndices.has(index)) {
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract a human-readable message from an agent invocation failure.
 * Prefers the API's detail/message over Axios's generic text.
 */
function classifyInvocationError(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const data = error.response?.data as
      | { detail?: string; message?: string }
      | string
      | undefined;

    if (status === 0 || !error.response) {
      return "Could not reach the API server. Make sure the API is running and try again.";
    }
    if (status === 404) {
      return `Agent not found (404). Check that VITE_ONBOARDING_AGENT_ID is set to a valid agent ID in your .env file.`;
    }
    if (status === 422 || status === 400) {
      const detail =
        typeof data === "object" && data !== null
          ? (data.detail ?? data.message)
          : typeof data === "string"
            ? data
            : undefined;
      return detail
        ? `Workflow payload rejected (${status}): ${detail}`
        : `Invalid workflow payload (${status}). The work_pattern_import workflow may not be registered on the running server.`;
    }
    if (status === 409) {
      return "This batch was already submitted. If the import completed, refresh the page. Otherwise, start a new import.";
    }
    if (status === 503) {
      return "Onboarding worker is not enabled (503). Set ONBOARDING_WORKER_ENABLED=true in the API .env and restart the worker.";
    }
    return `API error (${status ?? "network"}): ${error.message}`;
  }
  return `Unexpected error: ${error instanceof Error ? error.message : String(error)}`;
}

/** Map raw workflow poll errors to operator-friendly copy. */
export function classifyPollError(rawError: string): string {
  const normalized = rawError.toLowerCase();

  if (
    rawError.includes("AlreadyExistsError") ||
    normalized.includes("idempotency conflict") ||
    normalized.includes("idempotency-key")
  ) {
    return "This batch was already submitted. If the import completed, refresh the page. Otherwise, start a new import.";
  }

  if (rawError.includes("MyPayAuthError") || normalized.includes("mypay auth")) {
    return "Could not connect to payroll. Check your environment configuration and try again.";
  }

  if (rawError.includes("MyPayImportError")) {
    if (
      normalized.includes("500") ||
      normalized.includes("502") ||
      normalized.includes("503") ||
      normalized.includes("504")
    ) {
      return "Payroll returned a server error during import. Try again later.";
    }
    if (
      normalized.includes("timed out") ||
      normalized.includes("connection error") ||
      normalized.includes("connection refused")
    ) {
      return "Could not reach payroll. Check the connection and try again.";
    }
    return "Payroll rejected the import request. Review your file and try again.";
  }

  if (normalized.includes("activity task failed")) {
    return "The import workflow failed. Try again or contact support if the problem continues.";
  }

  return rawError;
}


/** Poll cadence, in ms. */
export const POLL_INTERVAL_MS = 2000;

/**
 * Overall polling bound. `useInvocationStatusPoll` (onboarding) polls until
 * terminal with no cap, so a workflow that never terminates would poll forever
 * and strand the user. Cap the local poll at ~5 minutes of 2s polls; once the
 * cap is hit we stop re-fetching and surface a timeout message.
 */
export const MAX_POLL_ATTEMPTS = 150;

export const POLL_TIMEOUT_MESSAGE =
  "The import is taking longer than expected. Refresh to check its status, or try again.";

const POLL_QUERY_KEY_ROOT = "workPatternImportInvocation";

function pollQueryKey(
  agentId: string | null | undefined,
  workflowId: string | null | undefined
) {
  return [POLL_QUERY_KEY_ROOT, agentId, workflowId] as const;
}

function useWorkPatternInvocationPoll(
  agentId: string | null | undefined,
  workflowId: string | null | undefined
) {
  return useQuery({
    queryKey: pollQueryKey(agentId, workflowId),
    queryFn: () =>
      api.getInvocationStatus(agentId as string, workflowId as string),
    enabled: !!agentId && !!workflowId,
    refetchInterval: (query) => {
      if (isTerminalInvocationStatus(query.state.data?.status)) return false;
      // Stop once the attempt cap is reached — a non-terminating workflow must
      // not be polled indefinitely.
      if (query.state.dataUpdateCount >= MAX_POLL_ATTEMPTS) return false;
      return POLL_INTERVAL_MS;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Orchestrates the work pattern import flow via the agent-invocations surface.
 *
 * Replaces the synchronous `useWorkPatternImportManager` (which called the
 * dedicated `POST /api/v1/onboarding/work-patterns` REST endpoint directly).
 * Now invokes the `work_pattern_import` Temporal workflow via
 * `POST /api/v1/agents/{id}/invocations` and polls for the result.
 *
 * Two invocations are made — one for validate, one for commit — following the
 * same two-phase UI flow. The result contract (`total`/`succeeded`/`failed`/`errors`)
 * is identical to the old REST response so no display-component changes are needed.
 *
 * Exposes the same `WorkPatternImportManagerReturn` interface as the old hook
 * so `WorkPatternImportInput` and `WorkPatternImportTable` need no changes.
 */
export function useWorkPatternImportInvocation(): WorkPatternImportManagerReturn {
  const queryClient = useQueryClient();
  const agentConfig = useCompanyProvisionAgentId();

  // ------------------------------------------------------------------
  // Parse / pending state — same as the old manager
  // ------------------------------------------------------------------

  const [pendingPatterns, setPendingPatterns] = useState<WorkPatternRow[]>(
    []
  );
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parseFailedRows, setParseFailedRows] = useState<
    WorkPatternImportParseFailedRow[]
  >([]);
  const [patternCsvRowNumbers, setPatternCsvRowNumbers] = useState<number[]>(
    []
  );
  const [parseWarnings, setParseWarnings] = useState<WorkPatternImportWarning[]>(
    []
  );
  const [companyCode, setCompanyCode] = useState<string | null>(null);
  const [scopeType, setScopeType] = useState<ScopeTypeOption | null>(null);
  const [mypayEnvironment, setMypayEnvironmentState] =
    useState<MyPayEnvironment | null>(null);
  const [rawCsvText, setRawCsvText] = useState<string | null>(null);
  const [dayColumnCountFromHeader, setDayColumnCountFromHeader] = useState<
    number | undefined
  >(undefined);
  /**
   * Imperative pre-flight errors only (no company code / scope type / agent not
   * configured). Poll-status errors, invoke failures and the poll timeout are
   * DERIVED during render — see `managerError` below.
   */
  const [guardError, setGuardError] = useState<string | null>(null);
  const [validationStale, setValidationStale] = useState(false);
  const [ignoredWarningIndices, setIgnoredWarningIndices] = useState<
    ReadonlySet<number>
  >(() => new Set());

  /** Synchronous ref mirrors — prevent React state-batching races */
  const pendingPatternsRef = useRef<WorkPatternRow[]>([]);
  const companyCodeRef = useRef<string | null>(null);
  const scopeTypeRef = useRef<ScopeTypeOption | null>(null);
  const mypayEnvironmentRef = useRef<MyPayEnvironment | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);
  /**
   * Monotonic revision of inputs that affect the validation payload. Validation
   * completions may only clear `validationStale` when they were submitted for
   * the current revision.
   */
  const validationInputRevisionRef = useRef(0);
  /** Latest validate submission; prevents older same-revision invokes winning a race. */
  const validationSubmissionSequenceRef = useRef(0);
  /** Latest commit submission; prevents stale invoke responses restoring cleared state. */
  const commitSubmissionSequenceRef = useRef(0);
  /** Revision associated with the workflow currently selected for polling. */
  const validateWorkflowRevisionRef = useRef<number | null>(null);
  /** Guards the commit cache-invalidation side effect to fire exactly once. */
  const commitInvalidatedRef = useRef(false);
  /**
   * Original indices (into pendingPatterns) of the patterns submitted to the
   * commit workflow. Set just before the commit invocation fires; used by the
   * commitResult memo to remap workflow error/warning indices.
   */
  const commitEligibleIndicesRef = useRef<number[]>([]);
  /**
   * Blocking-error rows that were skipped (not submitted) at commit time.
   * Injected into the commit result as failures so the report can display them.
   */
  const commitSkippedRowsRef = useRef<SkippedBlockingRow[]>([]);

  // ------------------------------------------------------------------
  // Invocation state — workflow IDs for polling
  // ------------------------------------------------------------------

  const [validateAgentId, setValidateAgentId] = useState<string | null>(null);
  const [validateWorkflowId, setValidateWorkflowId] = useState<string | null>(
    null
  );
  const [commitAgentId, setCommitAgentId] = useState<string | null>(null);
  const [commitWorkflowId, setCommitWorkflowId] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Polling
  // ------------------------------------------------------------------

  const validatePoll = useWorkPatternInvocationPoll(
    validateAgentId,
    validateWorkflowId
  );
  const commitPoll = useWorkPatternInvocationPoll(
    commitAgentId,
    commitWorkflowId
  );

  /** Clear stale validation only for the exact inputs this workflow validated. */
  useEffect(() => {
    if (
      validatePoll.data?.status === InvocationStatusEnum.COMPLETED &&
      validateWorkflowRevisionRef.current ===
        validationInputRevisionRef.current
    ) {
      setValidationStale(false);
    }
  }, [validatePoll.data?.status, validatePoll.dataUpdatedAt]);

  // The number of resolved polls (`dataUpdateCount`) lives on the query *state*,
  // not the `useQuery` result object. Read `dataUpdatedAt` (which changes on
  // every poll — `data` is referentially stable across identical statuses via
  // structural sharing) so each poll re-renders the hook, then read the
  // authoritative attempt count from the query cache.
  const validateAttempts = validatePoll.dataUpdatedAt
    ? (queryClient.getQueryState(pollQueryKey(validateAgentId, validateWorkflowId))
        ?.dataUpdateCount ?? 0)
    : 0;
  const commitAttempts = commitPoll.dataUpdatedAt
    ? (queryClient.getQueryState(pollQueryKey(commitAgentId, commitWorkflowId))
        ?.dataUpdateCount ?? 0)
    : 0;

  // A poll has "timed out" once it hit the attempt cap while still non-terminal.
  const validateTimedOut =
    !!validateWorkflowId &&
    !isTerminalInvocationStatus(validatePoll.data?.status) &&
    validateAttempts >= MAX_POLL_ATTEMPTS;
  const commitTimedOut =
    !!commitWorkflowId &&
    !isTerminalInvocationStatus(commitPoll.data?.status) &&
    commitAttempts >= MAX_POLL_ATTEMPTS;

  // ------------------------------------------------------------------
  // Derived result state — read straight from the poll data during render
  // (mirrors `useCompanyShellProvisionInvocation`), so there is no render-lag
  // and no mirrored state to reset manually across validate/commit/clear.
  // ------------------------------------------------------------------

  const validationReport = useMemo<WorkPatternImportResult | null>(() => {
    if (validatePoll.data?.status !== InvocationStatusEnum.COMPLETED) return null;
    const result = validatePoll.data.result;
    if (!isWorkPatternImportResult(result)) return null;

    const parseFailCount = parseFailedRows.length;
    return toLocalImportResult(result, parseFailCount);
  }, [validatePoll.data, parseFailedRows]);

  const commitResult = useMemo<WorkPatternImportResult | null>(() => {
    if (commitPoll.data?.status !== InvocationStatusEnum.COMPLETED) return null;
    const result = commitPoll.data.result;
    if (!isWorkPatternImportResult(result)) return null;

    const eligibleIndices = commitEligibleIndicesRef.current;
    const skippedRows = commitSkippedRowsRef.current;
    const parseFailCount = parseFailedRows.length;

    // If a partial commit was performed (some rows skipped), remap indices and
    // inject skipped rows. Otherwise use the simpler toLocalImportResult path.
    if (skippedRows.length > 0) {
      return toCommitImportResult(result, eligibleIndices, skippedRows, parseFailCount);
    }
    return toLocalImportResult(result, parseFailCount);
  }, [commitPoll.data, parseFailedRows.length]);

  // Error surfaced from a terminal poll status (FAILED/TERMINATED) or a
  // COMPLETED status whose payload didn't match the expected result shape.
  const pollError = useMemo<string | null>(() => {
    const validateStatus = validatePoll.data?.status;
    if (
      validateStatus === InvocationStatusEnum.COMPLETED &&
      !isWorkPatternImportResult(validatePoll.data?.result)
    ) {
      return "Unexpected response from validation workflow. Please try again.";
    }
    if (
      validateStatus === InvocationStatusEnum.FAILED ||
      validateStatus === InvocationStatusEnum.TERMINATED
    ) {
      const raw =
        validatePoll.data?.error ??
        "Validation workflow failed. Please try again.";
      return classifyPollError(raw);
    }

    const commitStatus = commitPoll.data?.status;
    if (
      commitStatus === InvocationStatusEnum.COMPLETED &&
      !isWorkPatternImportResult(commitPoll.data?.result)
    ) {
      return "Unexpected response from import workflow. Please try again.";
    }
    if (
      commitStatus === InvocationStatusEnum.FAILED ||
      commitStatus === InvocationStatusEnum.TERMINATED
    ) {
      const raw =
        commitPoll.data?.error ?? "Import workflow failed. Please try again.";
      return classifyPollError(raw);
    }

    return null;
  }, [validatePoll.data, commitPoll.data]);

  // Cache invalidation is a side effect (can't run during render), so keep a
  // single minimal effect for it — fired exactly once when the commit workflow
  // completes with a well-shaped result.
  useEffect(() => {
    if (
      commitPoll.data?.status === InvocationStatusEnum.COMPLETED &&
      isWorkPatternImportResult(commitPoll.data.result) &&
      !commitInvalidatedRef.current
    ) {
      commitInvalidatedRef.current = true;
      void queryClient.invalidateQueries({ queryKey: ["work-patterns"] });
    }
  }, [commitPoll.data, queryClient]);

  // ------------------------------------------------------------------
  // Mutations — fire-and-forget; polling drives UI state
  // ------------------------------------------------------------------

  const validateMutation = useMutation({
    mutationFn: (args: {
      agentId: string;
      serviceAccountId: string;
      companyCode: string;
      scopeType: ScopeTypeOption;
      patterns: WorkPatternRow[];
      idempotencyKey: string;
      mypayEnvironment: MyPayEnvironment;
      validationInputRevision: number;
      validationSubmissionSequence: number;
    }) =>
      api.invokeAgent(args.agentId, {
        service_account_id: args.serviceAccountId,
        input_payload: {
          workflow: "work_pattern_import",
          mode: "validate",
          scopeType: args.scopeType,
          company_code: args.companyCode,
          patterns: args.patterns,
          idempotency_key: args.idempotencyKey,
          mypay_environment: args.mypayEnvironment,
        } as WorkPatternImportInput,
      }),
    onSuccess: (data, variables) => {
      if (
        variables.validationInputRevision !==
          validationInputRevisionRef.current ||
        variables.validationSubmissionSequence !==
          validationSubmissionSequenceRef.current
      ) {
        return;
      }
      validateWorkflowRevisionRef.current =
        variables.validationInputRevision;
      setValidateAgentId(variables.agentId);
      setValidateWorkflowId(data.workflow_id);
    },
    // Invoke failures are read off `validateMutation.error` and folded into the
    // derived `managerError` below — no onError setState needed.
  });

  const commitMutation = useMutation({
    mutationFn: (args: {
      agentId: string;
      serviceAccountId: string;
      companyCode: string;
      scopeType: ScopeTypeOption;
      patterns: WorkPatternRow[];
      idempotencyKey: string;
      mypayEnvironment: MyPayEnvironment;
      commitInputRevision: number;
      commitSubmissionSequence: number;
    }) =>
      api.invokeAgent(args.agentId, {
        service_account_id: args.serviceAccountId,
        input_payload: {
          workflow: "work_pattern_import",
          mode: "commit",
          scopeType: args.scopeType,
          company_code: args.companyCode,
          patterns: args.patterns,
          idempotency_key: args.idempotencyKey,
          mypay_environment: args.mypayEnvironment,
        } as WorkPatternImportInput,
      }),
    onSuccess: (data, variables) => {
      if (
        variables.commitInputRevision !== validationInputRevisionRef.current ||
        variables.commitSubmissionSequence !==
          commitSubmissionSequenceRef.current
      ) {
        return;
      }
      setCommitAgentId(variables.agentId);
      setCommitWorkflowId(data.workflow_id);
    },
    // Invoke failures are read off `commitMutation.error` and folded into the
    // derived `managerError` below — no onError setState needed.
  });

  // Invoke-call failures live on the mutations until the next mutate/reset.
  const invokeError = validateMutation.error ?? commitMutation.error;
  const timeoutError =
    validateTimedOut || commitTimedOut ? POLL_TIMEOUT_MESSAGE : null;

  // Coherent precedence: an imperative guard first, then an invoke failure,
  // then the poll timeout, then a terminal poll-status error.
  const managerError =
    guardError ??
    (invokeError ? classifyInvocationError(invokeError) : null) ??
    timeoutError ??
    pollError;

  // ------------------------------------------------------------------
  // Public interface
  // ------------------------------------------------------------------

  const setPendingData = (
    patterns: WorkPatternRow[],
    errors: string[] = [],
    warnings: WorkPatternImportWarning[] = [],
    nextCompanyCode: string | null = null,
    nextScopeType: ScopeTypeOption | null = null,
    nextParseFailedRows: WorkPatternImportParseFailedRow[] = [],
    nextPatternCsvRowNumbers: number[] = [],
    nextRawCsvText: string | null = null,
    // Track 2 (Slice 5 workloads) multipart arg — Track 1 doesn't send a
    // file, so it's accepted here only to keep the shared
    // `WorkPatternImportManagerReturn.setPendingData` signature identical
    // across both tracks (see that interface's doc comment).
    _file?: File,
    nextDayColumnCountFromHeader?: number
  ) => {
    validationInputRevisionRef.current += 1;
    validateWorkflowRevisionRef.current = null;
    pendingPatternsRef.current = patterns;
    companyCodeRef.current = nextCompanyCode;
    scopeTypeRef.current = nextScopeType;
    setPendingPatterns(patterns);
    setParseErrors(errors);
    setParseFailedRows(nextParseFailedRows);
    setPatternCsvRowNumbers(nextPatternCsvRowNumbers);
    setParseWarnings(warnings);
    setCompanyCode(nextCompanyCode);
    setScopeType(nextScopeType);
    setRawCsvText(nextRawCsvText);
    setDayColumnCountFromHeader(nextDayColumnCountFromHeader);
    setValidationStale(false);
    setIgnoredWarningIndices(new Set());
    // Clear any prior results/errors by resetting their derived sources.
    setGuardError(null);
    setValidateWorkflowId(null);
    setValidateAgentId(null);
    setCommitWorkflowId(null);
    setCommitAgentId(null);
    validateMutation.reset();
    commitMutation.reset();
    commitInvalidatedRef.current = false;
  };

  const validate = async (): Promise<void> => {
    const patterns = pendingPatternsRef.current;
    const batchCompanyCode = companyCodeRef.current;
    const batchScopeType = scopeTypeRef.current;
    if (patterns.length === 0) return;

    if (!batchCompanyCode) {
      setGuardError(
        "No company code found. Please enter and validate a company code first."
      );
      return;
    }

    if (!batchScopeType) {
      setGuardError(
        "No scope type selected. Please select a scope type before importing."
      );
      return;
    }

    const batchMypayEnvironment = mypayEnvironmentRef.current;
    if (!batchMypayEnvironment) {
      setGuardError(
        "No Datapay environment selected. Please choose an environment before importing."
      );
      return;
    }

    if (!agentConfig.isConfigured) {
      setGuardError(
        "Work pattern import is not available in this environment."
      );
      return;
    }

    // Capture synchronously before invoking so an edit/removal that happens
    // while invoke or polling is in flight cannot make this result look fresh.
    const validationInputRevision = validationInputRevisionRef.current;
    const validationSubmissionSequence =
      ++validationSubmissionSequenceRef.current;

    // Mint a fresh idempotency key — reused by commit() for this batch.
    idempotencyKeyRef.current = crypto.randomUUID();

    // Reset both polls so stale derived results/errors don't flash.
    validateWorkflowRevisionRef.current = null;
    setGuardError(null);
    setValidateWorkflowId(null);
    setValidateAgentId(null);
    setCommitWorkflowId(null);
    setCommitAgentId(null);
    commitMutation.reset();
    commitInvalidatedRef.current = false;
    commitEligibleIndicesRef.current = [];
    commitSkippedRowsRef.current = [];

    validateMutation.mutate({
      agentId: agentConfig.agentId!,
      serviceAccountId: agentConfig.serviceAccountId!,
      companyCode: batchCompanyCode,
      scopeType: batchScopeType,
      patterns,
      idempotencyKey: idempotencyKeyRef.current,
      mypayEnvironment: batchMypayEnvironment,
      validationInputRevision,
      validationSubmissionSequence,
    });
  };

  const commit = async (
    options?: WorkPatternImportCommitOptions
  ): Promise<void> => {
    const patterns = pendingPatternsRef.current;
    const batchCompanyCode = companyCodeRef.current;
    const batchScopeType = scopeTypeRef.current;
    const batchMypayEnvironment = mypayEnvironmentRef.current;

    // Compute blocking indices from both server validation errors and client
    // parse errors so eligibility matches what the clustered report shows.
    const serverErrorIndices = new Set(
      (validationReport?.errors ?? []).map((e) => e.index)
    );
    const clientErrorIndices = clientParseErrorsByPatternIndex(
      parseErrors,
      patternCsvRowNumbers
    );
    const isBlocking = (index: number) =>
      serverErrorIndices.has(index) || clientErrorIndices.has(index);

    const eligibleIndices: number[] = [];
    const skippedRows: SkippedBlockingRow[] = [];
    patterns.forEach((pattern, index) => {
      if (isBlocking(index)) {
        const serverErrors = validationReport?.errors.filter(
          (e) => e.index === index
        );
        const clientMsgs = clientErrorIndices.get(index);
        const serverParts = (serverErrors ?? [])
          .map((se) => se.error)
          .filter((message): message is string => Boolean(message));
        const clientParts = clientMsgs ?? [];
        const mergedParts = mergeClientAndServerErrorParts(
          serverParts,
          clientParts
        );
        const error =
          mergedParts.length > 0 ? mergedParts.join("; ") : "Validation failed";
        skippedRows.push({
          originalIndex: index,
          workPatternName: pattern.workPatternName,
          error,
        });
      } else {
        eligibleIndices.push(index);
      }
    });

    const eligiblePatterns = eligibleIndices.map((i) => patterns[i]);

    const apiWarnings = (validationReport)?.warnings ?? [];
    const mergedWarnings = mergeWarningsByIndex(parseWarnings, apiWarnings);
    let effectiveIgnored: ReadonlySet<number> = ignoredWarningIndices;
    if (options?.overrideUnacknowledgedWarnings) {
      const nextIgnored = new Set(ignoredWarningIndices);
      for (const [index, messages] of mergedWarnings) {
        // Only auto-dismiss warnings on eligible rows.
        if ((messages?.length ?? 0) > 0 && eligibleIndices.includes(index)) {
          nextIgnored.add(index);
        }
      }
      setIgnoredWarningIndices(nextIgnored);
      effectiveIgnored = nextIgnored;
    }

    // Only block on unacknowledged warnings for eligible rows.
    const eligibleMergedWarnings = new Map<number, string[]>();
    for (const idx of eligibleIndices) {
      const msgs = mergedWarnings.get(idx);
      if (msgs) eligibleMergedWarnings.set(idx, msgs);
    }
    const unacknowledgedWarnings = computeHasUnacknowledgedWarnings(
      eligibleMergedWarnings,
      effectiveIgnored
    );

    if (
      eligiblePatterns.length === 0 ||
      idempotencyKeyRef.current === null ||
      !batchCompanyCode ||
      !batchScopeType ||
      !batchMypayEnvironment ||
      validationStale ||
      unacknowledgedWarnings
    )
      return;

    if (!agentConfig.isConfigured) {
      setGuardError(
        "Work pattern import is not available in this environment."
      );
      return;
    }

    // Snapshot eligible mapping and skipped rows for result remapping.
    commitEligibleIndicesRef.current = eligibleIndices;
    commitSkippedRowsRef.current = skippedRows;
    const commitInputRevision = validationInputRevisionRef.current;
    const commitSubmissionSequence = ++commitSubmissionSequenceRef.current;

    setGuardError(null);
    setCommitWorkflowId(null);
    setCommitAgentId(null);
    commitInvalidatedRef.current = false;

    commitMutation.mutate({
      agentId: agentConfig.agentId!,
      serviceAccountId: agentConfig.serviceAccountId!,
      companyCode: batchCompanyCode,
      scopeType: batchScopeType,
      patterns: eligiblePatterns,
      idempotencyKey: idempotencyKeyRef.current,
      mypayEnvironment: batchMypayEnvironment,
      commitInputRevision,
      commitSubmissionSequence,
    });
  };

  const clear = () => {
    validationInputRevisionRef.current += 1;
    validateWorkflowRevisionRef.current = null;
    pendingPatternsRef.current = [];
    companyCodeRef.current = null;
    scopeTypeRef.current = null;
    idempotencyKeyRef.current = null;
    commitEligibleIndicesRef.current = [];
    commitSkippedRowsRef.current = [];
    setPendingPatterns([]);
    setParseErrors([]);
    setParseFailedRows([]);
    setPatternCsvRowNumbers([]);
    setParseWarnings([]);
    setCompanyCode(null);
    setScopeType(null);
    setRawCsvText(null);
    setGuardError(null);
    setValidateWorkflowId(null);
    setValidateAgentId(null);
    setCommitWorkflowId(null);
    setCommitAgentId(null);
    validateMutation.reset();
    commitMutation.reset();
    commitInvalidatedRef.current = false;
    setValidationStale(false);
    setIgnoredWarningIndices(new Set());
  };

  const removePattern = (index: number) => {
    validationInputRevisionRef.current += 1;
    validateWorkflowRevisionRef.current = null;
    const next = pendingPatternsRef.current.filter((_, i) => i !== index);
    pendingPatternsRef.current = next;
    setPendingPatterns(next);
    setPatternCsvRowNumbers((prev) => prev.filter((_, i) => i !== index));
    // Clear results: after removing a row the old error indices no longer map
    // to the updated patterns array, which would corrupt the Pass/Fail column.
    // Results are derived from the polls, so reset the polls to clear them.
    setValidateWorkflowId(null);
    setValidateAgentId(null);
    setCommitWorkflowId(null);
    setCommitAgentId(null);
    commitInvalidatedRef.current = false;
    setParseWarnings((prev) =>
      prev
        .filter((warning) => warning.patternIndex !== index)
        .map((warning) =>
          warning.patternIndex > index
            ? { ...warning, patternIndex: warning.patternIndex - 1 }
            : warning
        )
    );
    setIgnoredWarningIndices((prev) => {
      const nextIgnored = new Set<number>();
      for (const ignoredIndex of prev) {
        if (ignoredIndex === index) continue;
        nextIgnored.add(
          ignoredIndex > index ? ignoredIndex - 1 : ignoredIndex
        );
      }
      return nextIgnored;
    });
    setValidationStale(true);
  };

  const updatePattern = (index: number, next: WorkPatternRow) => {
    validationInputRevisionRef.current += 1;
    validateWorkflowRevisionRef.current = null;
    const updated = pendingPatternsRef.current.map((p, i) =>
      i === index ? next : p
    );
    pendingPatternsRef.current = updated;
    setPendingPatterns(updated);
    setParseWarnings((prev) => {
      const existing = prev.find((w) => w.patternIndex === index);
      const without = prev.filter((w) => w.patternIndex !== index);
      const message = computeStandardValuesMismatchMessage(next);
      if (!message) {
        return without;
      }
      const csvRowNumber =
        existing?.rowNumber ?? patternCsvRowNumbers[index] ?? index + 2;
      return [
        ...without,
        {
          rowNumber: csvRowNumber,
          workPatternName: next.workPatternName,
          patternIndex: index,
          message,
        },
      ];
    });
    setIgnoredWarningIndices((prev) => {
      if (!prev.has(index)) {
        return prev;
      }
      const nextIgnored = new Set(prev);
      nextIgnored.delete(index);
      return nextIgnored;
    });
    setParseErrors((prev) =>
      recomputeParseErrorsAfterPatternEdit({
        prev,
        updated,
        editedIndex: index,
        patternCsvRowNumbers,
        dayColumnCountFromHeader,
      })
    );
    setValidationStale(true);
    setValidateWorkflowId(null);
    setValidateAgentId(null);
    setCommitWorkflowId(null);
    setCommitAgentId(null);
    commitInvalidatedRef.current = false;
    validateMutation.reset();
    commitMutation.reset();
  };

  const ignoreWarning = (index: number) => {
    setIgnoredWarningIndices((prev) => new Set(prev).add(index));
  };

  const setMyPayEnvironment = (environment: MyPayEnvironment | null) => {
    mypayEnvironmentRef.current = environment;
    setMypayEnvironmentState(environment);
  };

  // ------------------------------------------------------------------
  // Derived state
  // ------------------------------------------------------------------

  const isValidating =
    validateMutation.isPending ||
    (!!validateWorkflowId &&
      !isTerminalInvocationStatus(validatePoll.data?.status) &&
      !validateTimedOut);

  const isCommitting =
    commitMutation.isPending ||
    (!!commitWorkflowId &&
      !isTerminalInvocationStatus(commitPoll.data?.status) &&
      !commitTimedOut);

  const warningsByPatternIndex = useMemo(() => {
    const apiWarnings = (commitResult ?? validationReport)?.warnings ?? [];
    return mergeWarningsByIndex(parseWarnings, apiWarnings);
  }, [parseWarnings, commitResult, validationReport]);

  const hasUnacknowledgedWarnings = useMemo(
    () =>
      computeHasUnacknowledgedWarnings(
        warningsByPatternIndex,
        ignoredWarningIndices
      ),
    [warningsByPatternIndex, ignoredWarningIndices]
  );

  const validationEndedWithoutReport = useMemo(
    () =>
      computeValidationEndedWithoutReport({
        pendingPatternCount: pendingPatterns.length,
        validationReport,
        isValidating,
        isCommitting,
        validateWorkflowId,
        validateTimedOut,
        validatePollStatus: validatePoll.data?.status,
        validatePollResult: validatePoll.data?.result,
        validateInvokeFailed: validateMutation.isError,
      }),
    [
      pendingPatterns.length,
      validationReport,
      isValidating,
      isCommitting,
      validateWorkflowId,
      validateTimedOut,
      validatePoll.data?.status,
      validatePoll.data?.result,
      validateMutation.isError,
    ]
  );

  const hasUncommittedImportProgress = useMemo(() => {
    if (commitResult !== null) {
      return false;
    }
    return (
      pendingPatterns.length > 0 ||
      parseErrors.length > 0 ||
      parseFailedRows.length > 0 ||
      validationReport !== null ||
      isValidating ||
      isCommitting
    );
  }, [
    commitResult,
    pendingPatterns.length,
    parseErrors.length,
    parseFailedRows.length,
    validationReport,
    isValidating,
    isCommitting,
  ]);

  return {
    pendingPatterns,
    parseErrors,
    parseFailedRows,
    patternCsvRowNumbers,
    validationEndedWithoutReport,
    parseWarnings,
    warningsByPatternIndex,
    hasParseWarnings: warningsByPatternIndex.size > 0,
    companyCode,
    scopeType,
    mypayEnvironment,
    rawCsvText,
    validationReport,
    commitResult,
    validationStale,
    ignoredWarningIndices,
    hasUnacknowledgedWarnings,
    managerError,
    isValidating,
    isCommitting,
    hasData: pendingPatterns.length > 0,
    hasParseErrors: parseErrors.length > 0,
    hasUncommittedImportProgress,
    setPendingData,
    validate,
    commit,
    clear,
    removePattern,
    updatePattern,
    ignoreWarning,
    setMyPayEnvironment,
  };
}
